import { useCallback } from 'react'

import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'

import { ErrorBoundary } from '../../../common/components/ErrorBoundary/ErrorBoundary.js'
import { useMediaModal } from '../../../MediaModal/MediaModalProvider.js'
import { ChatAttachment } from '../../types.js'

import { ComposerFileAttachmentItem } from './ComposerFileAttachmentItem.js'
import { ComposerImageAttachmentItem } from './ComposerImageAttachmentItem.js'

interface Props {
  attachment: ChatAttachment
  isUploading: boolean
  onRemove: (attachment: ChatAttachment) => void
}

export const ComposerAttachmentItem = ({
  attachment,
  isUploading,
  onRemove,
}: Props) => {
  const { slidesRef, openSlideWithIndex } = useMediaModal()
  const onClick = useCallback(() => {
    const idx = slidesRef.current.findIndex(
      slide => slide.id === attachment.uploadedFile?.id,
    )
    if (idx >= 0) {
      openSlideWithIndex(idx)
    }
  }, [slidesRef, attachment.uploadedFile?.id, openSlideWithIndex])

  return (
    <button
      type="button"
      className="relative inline-flex shrink-0 items-center text-sm font-medium text-center rounded-lg bg-surface-subdued max-w-fit group cursor-pointer"
      onClick={onClick}
    >
      <ErrorBoundary>
        {attachment.type === 'image' ? (
          <ComposerImageAttachmentItem attachment={attachment} />
        ) : (
          <ComposerFileAttachmentItem attachment={attachment} />
        )}
      </ErrorBoundary>
      <div
        className="absolute top-0 right-0 translate-x-1/2 -translate-y-1/2 z-10 bg-surface-subdued rounded-full p-1 hover:bg-surface-subdued-hovered opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity duration-200"
        onClick={e => {
          e.stopPropagation()
          onRemove(attachment)
        }}
      >
        <SvgIcon name="close" />
      </div>
      {isUploading && (
        <div
          role="status"
          className="absolute -translate-x-1/2 -translate-y-1/2 top-2/4 left-1/2 h-full w-full flex items-center justify-center rounded-lg"
          style={{
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
          }}
        >
          <SvgIcon className="animate-spin w-7 h-7" name="spinner" />
        </div>
      )}
    </button>
  )
}
